import api from './api';

/*
 * API client for SUGGESTIONS — "suggest a gift idea" person-to-person flow.
 *
 * One member suggests an idea to another ("would you be interested?"). The
 * recipient ACCEPTS → the idea is minted as a normal wish on their own list, or
 * DECLINES → the sender sees it declined. A suggestion is a self-contained
 * snapshot with a small status machine (pending → accepted/declined/withdrawn);
 * it is NOT a wish until accepted. Full design: ../listful-api/docs/SUGGESTIONS_PLAN.md.
 *
 * Endpoints (all under auth:sanctum, /api/v1):
 *   GET    /suggestions?box=received&status=pending  inbox (recipient)
 *   GET    /suggestions?box=sent                     sent list w/ statuses (sender)
 *   GET    /suggestions/unread-count                 { count } for the nav badge
 *   POST   /suggestions                              send an idea → 201 suggestion
 *   POST   /suggestions/{id}/accept                  → { message, wish, suggestion }
 *   POST   /suggestions/{id}/decline                 → { message }
 *   POST   /suggestions/{id}/seen                    mark seen (clears the badge)
 *   DELETE /suggestions/{id}                         withdraw (sender, pending only)
 *
 * Each function returns the already-unwrapped payload: the shared axios instance
 * (services/api.js) strips the { success, data, ... } envelope, so `res.data`
 * here is the plain object/array the controller returned. This mirrors
 * services/people.js / services/pets.js.
 */

// GET /suggestions?box=received&status=pending — the recipient's pending inbox.
export async function inbox() {
    const res = await api.get('/suggestions', { params: { box: 'received', status: 'pending' } });
    return res.data;
}

// GET /suggestions?box=sent — the sender's list, every status.
export async function sent() {
    const res = await api.get('/suggestions', { params: { box: 'sent' } });
    return res.data;
}

// GET /suggestions/unread-count — { count } of unseen pending suggestions.
export async function unreadCount() {
    const res = await api.get('/suggestions/unread-count');
    return res.data;
}

// POST /suggestions — send an idea. `payload` = { recipient_user_id, group_id,
// source_wish_id?, title, description?, url?, image_url?, price_min?, price_max?,
// category_id?, message? }. Returns the created suggestion (relations loaded).
export async function createSuggestion(payload) {
    const res = await api.post('/suggestions', payload);
    return res.data;
}

// POST /suggestions/{id}/accept — recipient accepts; mints a wish on their list.
export async function acceptSuggestion(id) {
    const res = await api.post(`/suggestions/${id}/accept`);
    return res.data;
}

// POST /suggestions/{id}/decline — recipient declines.
export async function declineSuggestion(id) {
    const res = await api.post(`/suggestions/${id}/decline`);
    return res.data;
}

// DELETE /suggestions/{id} — sender withdraws a still-pending suggestion.
export async function withdrawSuggestion(id) {
    const res = await api.delete(`/suggestions/${id}`);
    return res.data;
}

// POST /suggestions/{id}/seen — recipient marks it seen (clears the nav badge).
export async function markSeen(id) {
    const res = await api.post(`/suggestions/${id}/seen`);
    return res.data;
}
